import {DatePicker} from "./DatePicker.js";

/**
 * Standalone component used by {@link DateTimeEditor} or in Struts forms to render
 * form date-time input.
 * It supports dynamic dates.
 */

/** Keep in sync with com.anneca.intouch.api.datetime.TimePolicy on the server */
export const TimePolicy = {
	DATE : 0,	// time is not allowed
	UNIVERSAL: 1, // time is allowed but date is preferred
	DATETIME: 2, // time is required
	allowTime: function(policy) { return policy > TimePolicy.DATE; }
};

const registry = new Map();

const DEBUG = false;
const SYMBOLIC_REGEX = new RegExp("^(NOW|((D|WM|WS|M|Q|Y)(START|END)))\\(-?\\d{0,3}\\)$", "i");

export class DateTimeInput {
	/**
	 * @param {string|jQuery} input - field with formatted value
	 * @param {function} getter - function to get ISO date value (should return string}
	 * @param {function} setter - function to set ISO date value (receives string argument)
	 * @param {TimePolicy} timePolicy - how to handle time
	 * @param {boolean} symbolic - user can enter symbolic date values
	 */
	constructor(input, getter, setter, timePolicy, symbolic) {
		if (typeof input === 'string') this.input = $('#' + input);
		else this.input = input;
		this.input.attr("autocomplete", "off");
		this.getter = getter;
		this.setter = setter;
		this.timePolicy = timePolicy;
		this.symbolic = symbolic;
		this.container = null;

		this.dateLocale = Server.date;
		this.dateRegex = new RegExp(Server.date.regex);
		this.dateTimeLocale = Server.datetime;
		this.dateTimeRegex = new RegExp(Server.datetime.regex);
		this.defaultHour = 12;	// when picking day but the field require time

		this.bind();
		this.forceHidden = false;	// keep picker hidden even if focused
	}

	get data() {
		return this.getter();
	}

	set data(d) {
		if (this.debug) this.debug.text(d === null || d.length === 0 ? "<empty>" : d);
		return this.setter(d);
	}

	/**
	 * @return {boolean}
	 */
	get disabled() {
		return this.input.prop("disabled");
	}

	/**
	 * @param {boolean} dis
	 */
	set disabled(dis) {
		this.input.prop("disabled", dis);
	}

	/**
	 * @return {boolean}
	 */
	get readonly() {
		return this.input.prop("readonly");
	}

	/**
	 * @param {boolean} ro
	 */
	set readonly(dis) {
		//this.input.prop("readonly", dis);
		if (dis) this.input.attr("readonly", "readonly");
		else this.input.removeAttr("readonly");
	}

	bind() {
		this.input.attr("placeholder", this.buildPlaceholder());
		if (DEBUG) {
			this.debug = $('<span style="font-size:12px;margin-left:4px;font-family:monospace"></span>');
			this.input.parent().append(this.debug);
		} else this.debug = null;

		const my = this.determineMonth();
		this.picker = new DatePicker(this.input, this.container, my.month, my.year, this.symbolic);
		this.picker.addDatePickListener(this.onDatePick.bind(this));
		this.picker.addSymbolicPickListener(this.onSymbolicPick.bind(this));

		// more bindings
		this.input.on('focus', this.onFocus.bind(this))
			.on('blur', this.onBlur.bind(this))
			.on('input', this.updateData.bind(this));
	}

	buildPlaceholder() {
		if (this.timePolicy === TimePolicy.DATE) return this.dateLocale.pattern;
		else if (this.timePolicy === TimePolicy.DATETIME) return this.dateTimeLocale.pattern;
		return this.dateTimeLocale.pattern.replace(/H+:m+/, "($&)");
	}

	/**
	 * User pick a date from the drop down
	 * @param {number} year
	 * @param {number} month
	 * @param {number} date
	 */
	onDatePick(year, month, date) {
		let dtObj = DateUtil.parseDate(this.data);
		if (dtObj.date === null) {
			dtObj.date = new Date();
			dtObj.wholeDay = this.timePolicy === TimePolicy.DATE || this.timePolicy === TimePolicy.UNIVERSAL;
			if (!dtObj.wholeDay) dtObj.date.setHours(this.defaultHour, 0, 0, 0);
		} else {
			if (this.timePolicy === TimePolicy.DATE) dtObj.wholeDay = true;
			else if (this.timePolicy === TimePolicy.DATETIME && dtObj.wholeDay) {
				dtObj.wholeDay = false;
				dtObj.date.setHours(this.defaultHour, 0, 0, 0);
			}
		}
		let possibleDate = DateTimeInput.daysInMonth(month, year);
		dtObj.date = new Date(year, month,
			date > 0 ? Math.min(possibleDate, date) : Math.min(dtObj.date.getDate(), possibleDate),
			dtObj.date.getHours(), dtObj.date.getMinutes(), 0, 0);
		this.data = DateUtil.formatDate(dtObj.date, !dtObj.wholeDay);
		this.updateScreen();
		const focusMethod = dtObj.wholeDay ? this.focusOnDate : this.focusOnTime;
		this.forceHidden = date > 0;	// force hidden only if concrete day was picked
		setTimeout(focusMethod.bind(this), 10);
	}

	/**
	 * User pick symbolic (dynamic) expression from the drop down
	 * @param {string} year
	 */
	onSymbolicPick(symbol) {
		this.data = symbol;
		this.updateScreen();
	}

	static daysInMonth(month, year) {
		let m = month+1;
		if (m > 11) {
			m = 0;
			year++;
		}
		return new Date(year, m, 0).getDate();
	}

	onFocus() {
		if (this.forceHidden || this.readonly || this.disabled) {
			this.picker.shouldBeHidden(true);
		} else {
			const d = this.data;
			let ops = {
				value: d,
				symbolic: SYMBOLIC_REGEX.test(d)
			};
			this.picker.shouldBeVisible(ops);
		}
	}

	onBlur() {
		this.picker.shouldBeHidden(false);
		this.forceHidden = false;
	}

	determineMonth() {
		let dtObj = DateUtil.parseDate(this.data);
		let theDate;
		if (dtObj.date) theDate = dtObj.date;
		else theDate = new Date();
		return {month: theDate.getMonth(), year: theDate.getFullYear()};
	}


	focusOnDate() {
		this.focusOnSym('d');
	}

	focusOnTime() {
		this.focusOnSym('H');
	}

	/** use d, M, y, H, m as a sym */
	focusOnSym(sym) {
		const str = this.input.val();
		if (str != null && str.trim().length > 0) {
			let numIdx = null;
			let m = this.dateTimeRegex.exec(str);
			if (m !== null) {
				numIdx = this.dateTimeLocale[sym];
			} else {
				m = this.dateRegex.exec(str);
				if (m !== null) {
					numIdx = this.dateLocale[sym];
				}
			}
			if (numIdx != null) {
				const ni = this.calcNumSpans(str);
				if (ni.length >= numIdx) {
					const span = ni[numIdx - 1];
					const input = this.input.get(0);
					input.setSelectionRange(span.start, span.end);
				}
			}
		}
		this.input.focus();
	}

	/** Return array of number spans */
	calcNumSpans(str) {
		const digits = '0123456789';
		const result = [];
		let state = 'waitingForDigit';
		let span = {};
		for (let i = 0; i < str.length; i++) {
			const ch = str.charAt(i);
			switch (state) {
				case 'waitingForDigit':
					if (digits.indexOf(ch) >= 0) {
						span.start = i;
						state = 'inNum';
					}
					break;
				case 'inNum':
					if (digits.indexOf(ch) < 0) {
						span.end = i;
						result.push(span);
						span = {};
						state = 'waitingForDigit';
					}
					break;
			}
		}
		if (state === 'inNum') {
			span.end = str.length;
			result.push(span);
		}
		return result;
	}

	moveToDate(day) {
		let dtObj = DateUtil.parseDate(this.data);
		if (dtObj.date) {
			dtObj.date.setFullYear(day.getFullYear());
			dtObj.date.setMonth(day.getMonth());
			dtObj.date.setDate(day.getDate());
		} else {
			dtObj.date = day;
			dtObj.wholeDay = !this.timeAllowed();
		}
		const newValue = DateUtil.formatDate(dtObj.date, !dtObj.wholeDay);
		this.data = newValue;
		this.updateScreen();
		setTimeout(this.focusOnTime.bind(this), 10);
	}


	timeAllowed() {
		return this.timePolicy !== TimePolicy.DATE;
	}

	updateScreen() {
		const d = this.data;
		this.input.val(this.fromData(d));
		if (this.debug) this.debug.text(d === null || d.length === 0 ? "<empty>" : d);
	}

	updateData() {
		const result = this.parseData(this.input.val());
		if (result.valid) {
			this.data = result.data;
			this.input.removeClass("junk");
			if (result.symbolic) this.picker.symbolicDateChanged(result.data);
			else this.picker.dateChanged(result.year, result.month, result.day);
		} else this.input.addClass("junk");
	}

	fromData(d) {
		// Format for user
		if (SYMBOLIC_REGEX.test(d)) return d;
		let dtObj = DateUtil.parseDate(d);
		if (dtObj.date) {
			return this.format(dtObj.date, dtObj.wholeDay);
		}
		return '';
	}

	/**
	 * @return {{ valid: boolean, [symbolic]: boolean, [data]: string, [year]: number, [month]: number, [day]: number }}
	 */
	parseData(d) {
		let result = { valid: false };
		if (this.symbolic && SYMBOLIC_REGEX.test(d)) {
			result.valid = true;
			result.symbolic = true;
			result.data = d;
		} else {
			result.symbolic = false;
			let dtObj = this.parse(d);
			result.valid = dtObj.valid;
			if (dtObj.valid) {
				if (dtObj.date === null) result.data = "";
				else {
					result.data = DateUtil.formatDate(dtObj.date, !dtObj.wholeDay);
					result.year = dtObj.date.getFullYear();
					result.month = dtObj.date.getMonth();
					result.day = dtObj.date.getDate();
				}
			}
		}
		return result;
	}

	format(date, wholeDay) {
		return DateTimeInput.formatDate(date, wholeDay ? this.dateLocale.pattern : this.dateTimeLocale.pattern);
	}

	/**
	 * Format Date based on pattern
	 * @param {Date} date
	 * @param {string} pattern - formating pattern
	 * @return {string} formated string
	 */
	static formatDate(date, pattern) {
		if (date === null) return '';
		const year = date.getFullYear().toString();
		let month = (date.getMonth()+1).toString();
		let day = date.getDate().toString();
		let hours = date.getHours().toString();
		let minutes = date.getMinutes().toString();
		let zmonth = month.length === 1 ? '0'+month : month;
		let zday = day.length === 1 ? '0'+day : day;
		let zhours = hours.length === 1 ? '0'+hours : hours;
		let zminutes = minutes.length === 1 ? '0'+minutes : minutes;
		return pattern.replace('yyyy', year)
			.replace('MM', zmonth)
			.replace('dd', zday)
			.replace('HH', zhours)
			.replace('mm', zminutes)
			.replace('M', month)
			.replace('d', day)
			.replace('H', hours)
			.replace('m', minutes);
	}

	/**
	 * @param {string} str
	 * @return {{valid: boolean, wholeDay: boolean, date: Date}}
	 */
	parse(str) {
		const result = { valid: false, wholeDay: false, date: null };
		if (str == null || str.trim().length === 0) {
			result.date = null;
			result.valid = true;	// empty string is OK
		} else {
			let m = TimePolicy.allowTime(this.timePolicy) ? this.dateTimeRegex.exec(str) : null; //try datetime regex only if allowed by time policy
			if (m === null) {
				// not date time, try date only
				m = this.timePolicy === TimePolicy.DATETIME ? null : this.dateRegex.exec(str);
				if (m === null) {
					if (TimePolicy.allowTime(this.timePolicy)) m = /^\s*([+\-]?\d{1,2}y)?\s?([+\-]?\d{1,3}m)?\s?([+\-]?\d{1,4}w)?\s?([+\-]?\d{1,5}d)?\s?(\d{1,2}:\d{2})?\s*$/.exec(str);
					else m = /^\s*([+\-]?\d{1,2}y)?\s?([+\-]?\d{1,3}m)?\s?([+\-]?\d{1,4}w)?\s?([+\-]?\d{1,5}d)?()\s*$/.exec(str);

					if (m === null) {
						result.date = null;
					} else {
						// Relative distance
						result.valid = true;

						const dayShift = DateTimeInput.parseShift(m[4]);
						const weekShift = DateTimeInput.parseShift(m[3]);
						const monthShift = DateTimeInput.parseShift(m[2]);
						const yearShift = DateTimeInput.parseShift(m[1]);
						const time = DateTimeInput.parseTime(m[5]);
						let d = new Date();
						d = new Date(d.getFullYear() + yearShift, d.getMonth() + monthShift, d.getDate() + dayShift + 7 * weekShift);

						if (time !== null) {
							d.setHours(time.hours);
							d.setMinutes(time.minutes);
							result.wholeDay = false;
						} else {
							d.setHours(0);
							d.setMinutes(0);
							result.wholeDay = true;
						}
						d.setSeconds(0);
						result.date = d;
					}
				} else {
					result.valid = true;
					let d = new Date(parseInt(m[this.dateLocale.y]), parseInt(m[this.dateLocale.M])-1, parseInt(m[this.dateLocale.d]));
					d.setHours(0);
					d.setMinutes(0);
					d.setSeconds(0);
					result.date = d;
					result.wholeDay = true;
				}
			} else {
				result.valid = true;
				let d = new Date(parseInt(m[this.dateTimeLocale.y]), parseInt(m[this.dateTimeLocale.M])-1, parseInt(m[this.dateTimeLocale.d]));
				d.setHours(parseInt(m[this.dateTimeLocale.H]));
				d.setMinutes(parseInt(m[this.dateTimeLocale.m]));
				d.setSeconds(0);
				result.date = d;
				result.wholeDay = false;
			}
		}
		return result;
	}

	static parseShift(sh) {
		if (sh == null || sh.length === 0) return 0;
		return parseInt(sh);
	}

	static parseTime(timeStr) {
		if (timeStr == null || timeStr.length === 0) return null;
		const res = { hours: 0, minutes: 0 };
		const dot = timeStr.indexOf(':');
		res.hours = parseInt(timeStr.substring(0, dot));
		res.minutes = parseInt(timeStr.substring(dot + 1));
		return res;
	}

	/**
	 * @param {string} hiddenInputId - ID of input field with hidden ISO value
	 * @param {string} formattedInputId - ID of a input with formatted string
	 * @param {TimePolicy} timePolicy - how to handle time
	 * @param {boolean} symbolic - user can enter symbolic date values
	 * @return {DateTimeInput}
	 */
	static init(hiddenInputId, formattedInputId, timePolicy, symbolic) {
		const hiddenInput = $('#'+hiddenInputId);
		const valFunc = hiddenInput.val.bind(hiddenInput);
		return new DateTimeInput($('#' + formattedInputId), valFunc, valFunc, timePolicy, symbolic);
	}

	/**
	 * Register this dt picker for given name key
	 * @param {string} nameKey
	 */
	register(nameKey) {
		registry.set(nameKey, this);
	}

	/**
	 * Return previously registered picker
	 * @param {string} nameKey
	 * @return {DateTimeInput}
	 */
	static find(nameKey) {
		return registry.get(nameKey);
	}
}

/***** GLOBAL METHOD FOR COMPATIBILITY REASONS *****/


function validInstance(property) {
	const instance = DateTimeInput.find(property);
	if (!instance) throw "Date time picker not found for property "+property;
	return instance;
}

/**
 * @param {string} property
 * @param {number} millis
 * @param {boolean} withTime
 * @deprecated
 */
window.setDateTimePicker = function(property, millis, withTime) {
	const instance = validInstance(property);
	const d = new Date();
	d.setTime(millis);
	instance.data = DateUtil.formatDate(d, withTime);
	instance.updateScreen();
};
/**
 * @param {string} property
 */
window.clearDateTimePicker = function(property) {
	const instance = validInstance(property);
	instance.data = "";
	instance.updateScreen();
};
/**
 *
 * @param {string} property
 */
window.getDateTimePicker = function(property) {
	return validInstance(property).data;
};
/**
 * @param {string} property
 * @param {boolean} disable
 */
window.getDateTimeDisabled = function(property, disable) {
	validInstance(property).disabled = disable;
};
/**
 * Install inside special purpose <code> element
 * <code property="" date="" time="" withTime="" withSym="" param="" tabindex="" readonly=""></code>
 * @param {string} elementId
 */
export function installDateTimePicker(elementId) {

	const elm = $('#'+elementId);
	if (elm.length !== 1) throw "Invalid element ID "+elementId+" for installing date time picker";
	elm.css("display", "inline");
	let property = elm.attr("property");
	let date = elm.attr("date");
	let time = elm.attr("time");
	let param = elm.attr("param");
	let withTime = elm.attr("withTime") === 'true';
	let withSym = elm.attr("withSym") === 'true';
	let tabindex = elm.attr("tabindex");
	let ro = elm.attr("readonly");

	let hidden = $('<input type="hidden" name="'+property+'">');
	elm.append(hidden);
	let formatted = $('<input type="text" name="'+property+'-formatted">');
	if (date) {
		if (time) date = date + " " + time;
		formatted.attr("value", date);
	}
	if (ro && (ro === "true" || ro === "readonly")) formatted.prop("readonly", true);
	if (tabindex) formatted.attr("tabindex", tabindex);
	elm.append(formatted);

	const valFunc = hidden.val.bind(hidden);
	const instance =  new DateTimeInput(formatted, valFunc, valFunc, withTime ? TimePolicy.DATETIME : TimePolicy.DATE, withSym);
	instance.register(property);
	instance.updateData();	// propagate value to the hidden input
	return instance;
};

$(function() {
	if (typeof window.afterPickerInit === 'function') {
		window.afterPickerInit();	// method called from GWT version of the picker
	}
});
